import React, { useEffect, useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "authConfig/authConfig";
import CloseCrossIcon from "assets/icons/closeCross.svg?react";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogBody,
} from "@material-tailwind/react";
import { Button } from "components/shared/Buttons";
import WarningIcon from "assets/icons/ForgotPass.svg?react";
import t from "utils/i18n";
import { useLogout } from "hooks/useLogout";

const IDLE_WARNING_MS = 60 * 60 * 1000; // 1 hour in ms
const TOKEN_REFRESH_THRESHOLD_MS = 60 * 1000; // 1 min before expiry

const TokenMonitor: React.FC = () => {
  const { instance } = useMsal();
  const { logout } = useLogout();
  const [showModal, setShowModal] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);
  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const lastActivity = useRef(Date.now());

  // Track user activity
  useEffect(() => {
    const resetIdleTimer = () => {
      lastActivity.current = Date.now();
      //setShowModal(false)
    };

    window.addEventListener("mousemove", resetIdleTimer);
    window.addEventListener("keydown", resetIdleTimer);
    window.addEventListener("mousedown", resetIdleTimer);
    window.addEventListener("touchstart", resetIdleTimer);
    window.addEventListener("scroll", resetIdleTimer);

    return () => {
      window.removeEventListener("mousemove", resetIdleTimer);
      window.removeEventListener("keydown", resetIdleTimer);
      window.removeEventListener("mousedown", resetIdleTimer);
      window.removeEventListener("touchstart", resetIdleTimer);
      window.removeEventListener("scroll", resetIdleTimer);
    };
  }, []);

  // Monitor token expiry and idle state
  useEffect(() => {
    const checkTokenAndIdle = async () => {
      if (loggedOut) return;
      const account = instance.getActiveAccount();
      if (!account) return;

      const idTokenClaims = account.idTokenClaims as any;
      const exp = idTokenClaims?.exp;
      if (exp) {
        //setExpiryTime(exp * 1000)
        const timeLeft = exp * 1000 - Date.now();
        const idleTime = Date.now() - lastActivity.current;

        if (timeLeft <= 0) {
          setShowModal(false);
          setLoggedOut(true);
          await logout();
        } else if (idleTime >= IDLE_WARNING_MS) {
          setShowModal(true);
        } else if (timeLeft < TOKEN_REFRESH_THRESHOLD_MS) {
          // User is active, refresh token silently
          try {
            await instance.acquireTokenSilent({
              account,
              scopes: ["user.read"],
            });
          } catch {
            instance.loginRedirect(loginRequest);
          }
        }
      }
    };

    idleTimer.current = setInterval(checkTokenAndIdle, 10000);
    return () => {
      if (idleTimer.current) clearInterval(idleTimer.current);
    };
  }, [instance, loggedOut, logout]);

  const handleRefresh = async () => {
    const account = instance.getActiveAccount();
    if (!account) return;
    try {
      await instance.acquireTokenSilent({
        account,
        scopes: ["user.read"],
      });
      setShowModal(false);
      lastActivity.current = Date.now();
    } catch {
      instance.loginRedirect(loginRequest);
    }
    console.log("Token refreshed");
  };

  const handleLogout = async () => {
    setShowModal(false);
    setLoggedOut(true);
    await logout();
  };
  const onClose = () => {
    setShowModal(false);
  };

  return (
    <Dialog
      {...({} as any)}
      className="flex flex-col gap-2 p-6 pt-6 text-base rounded-[4px] bg-elevation-1"
      open={showModal}
      handler={setShowModal}
      size="sm"
      dismiss={{
        escapeKey: false,
        outsidePress: false,
      }}
    >
      <DialogHeader
        {...({} as any)}
        className="flex items-center justify-between p-0"
      >
        <div className="flex items-center gap-2">
          <WarningIcon />
          <span>{t("tokenMonitor.heading")}</span>
        </div>
        <Button variant={"Tertiary"} onClick={onClose}>
          <CloseCrossIcon />
        </Button>
      </DialogHeader>
      <DialogBody {...({} as any)} className="flex flex-col gap-6 p-0">
        <span className="text-black text-lg">{t("tokenMonitor.body")}</span>
      </DialogBody>
      <DialogFooter {...({} as any)} className="flex gap-6 p-0 pt-6">
        <Button size="lg" variant="Secondary" onClick={handleLogout}>
          {t("tokenMonitor.cancel")}
        </Button>
        <Button size="lg" type="submit" onClick={handleRefresh}>
          {t("tokenMonitor.refresh")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default TokenMonitor;
